import { definedMap } from "collection-utils";
import { nullToUndefined } from "../support/index";
import { GlideDateTime } from "./glide-date-time";
import { sanitizeDateTime } from "./support";

// Functions in this file used to be part of Support.ts, but they've all been
// banished out here due to this async chrono-node import.
//
// The await import completely breaks Webpack when trying to build
// the service worker.

let chrono: any;

const parsedDates = new Map<string, GlideDateTime | undefined>();

type ParsedDateType<T extends boolean> = T extends true ? GlideDateTime : Date;

export function parseUserDateTimeZoneAgnosticSync<T extends boolean>(
    str: string,
    glideDateTime: T
): ParsedDateType<T> | undefined {
    if (chrono === undefined) {
        return undefined;
    }

    if (parsedDates.has(str)) {
        const d = parsedDates.get(str);
        if (glideDateTime) {
            return d as ParsedDateType<T> | undefined;
        } else {
            return d?.asTimeZoneAgnosticDate() as ParsedDateType<T> | undefined;
        }
    }

    if (parsedDates.size > 10000) {
        parsedDates.clear();
    }

    const sanitized = sanitizeDateTime(str, true);
    const parsed = definedMap(nullToUndefined(chrono.en.parseDate(sanitized)), d =>
        GlideDateTime.fromTimeZoneAwareDate(d)
    );

    parsedDates.set(str, parsed);

    if (glideDateTime) {
        return parsed as ParsedDateType<T> | undefined;
    } else {
        return parsed?.asTimeZoneAgnosticDate() as ParsedDateType<T> | undefined;
    }
}

async function importChronoAndParseDate<T extends boolean>(
    str: string,
    glideDateTime: T
): Promise<ParsedDateType<T> | undefined> {
    // @ts-ignore
    chrono = await import("chrono-node");
    return parseUserDateTimeZoneAgnosticSync(str, glideDateTime);
}

export function parseUserDateTimeZoneAgnostic<T extends boolean>(
    str: string,
    glideDateTime: T
): Promise<ParsedDateType<T> | undefined> | ParsedDateType<T> | undefined {
    if (chrono === undefined) {
        return importChronoAndParseDate(str, glideDateTime);
    }
    return parseUserDateTimeZoneAgnosticSync(str, glideDateTime);
}
